/**
 * TestBuilder — standalone Test creation + editor page (Phase 118.A.1).
 *
 * Routes:
 *   /tests/new?unit_activity_id=...  → setup → generate → edit
 *   /tests/:testId                   → edit existing
 *
 * The page is a controlled three-phase flow:
 *   1. Setup — pick question types + counts, optional weighting hint
 *   2. Generating — spinner while Claude works (one shot, no polling)
 *   3. Editing — every question editable inline; download both PDFs
 *
 * Per the spec: AI drafts, teacher decides. The teacher can edit any
 * field of any question, add new ones, delete any, change the
 * achievement category, change point values.
 */

import { useEffect, useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ClipboardCheck,
  Download,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
} from "lucide-react";
import AppShell from "../components/shell/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { api, listActivities, listFolders } from "../lib/api";
import {
  generateTest,
  getTest,
  saveTest,
  regenerateTest,
  QUESTION_TYPES,
  ACHIEVEMENT_CATEGORIES,
} from "../lib/tests";
import { api as _api } from "../lib/api";
import { useCurriculum, getGradesFromCurriculum, getSubjectsForGrade, getStrandsForGradeSubject } from "../lib/curriculum";
import LessonGroundingPicker from "../components/LessonGroundingPicker";
import SaveToFolderModal from "../components/SaveToFolderModal";

// Phase 118.A.2 — sessionStorage bridge consumed by the Test Builder
// after a successful generation. Set by `UnitSummativeSection` when
// the teacher picks the Test summative model so the builder can
// auto-attach the new test back to the unit-summative on save.
const PENDING_ATTACH_KEY = "atomicedu_pending_unit_summative_attach_test";

const _readPendingAttach = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_ATTACH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.unit_activity_id === "string") return parsed;
  } catch (_) {/* malformed bridge — ignore */}
  return null;
};

const _clearPendingAttach = () => {
  try {
    sessionStorage.removeItem(PENDING_ATTACH_KEY);
  } catch (_) {/* ignored */}
};

// ----------------------------------------------------------------------------
//  Setup phase — type counts
// ----------------------------------------------------------------------------

const DEFAULT_COUNTS = {
  multiple_choice: 6,
  true_false: 4,
  matching: 1,
  short_answer: 2,
  long_answer: 1,
  labeling: 0,
};

const SetupForm = ({ unitActivityId, standalone, onGenerated }) => {
  const { data: curriculum } = useCurriculum();
  const [counts, setCounts] = useState(DEFAULT_COUNTS);
  const [weighting, setWeighting] = useState("");
  const [busy, setBusy] = useState(false);
  // Phase 119.B — standalone-only fields (ignored when launched in-unit).
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [strand, setStrand] = useState("");
  const [reportingPeriod, setReportingPeriod] = useState("term_1");
  const [title, setTitle] = useState("");
  // Phase 119.A — taught-lesson grounding (shared between in-unit and standalone).
  const [attachedLessonIds, setAttachedLessonIds] = useState([]);
  // Phase 119.D — when launched in-unit, auto-attach the parent unit's
  // already-generated child lessons. The picker is collapsed by
  // default so the teacher sees the attached pills first; an "Attach
  // more from library" toggle expands the full library picker.
  const [autoAttachedCount, setAutoAttachedCount] = useState(0);
  useEffect(() => {
    if (standalone || !unitActivityId) return;
    let cancelled = false;
    (async () => {
      try {
        const { listUnitLessons } = await import("../lib/api");
        const children = await listUnitLessons(unitActivityId);
        if (cancelled) return;
        const ids = (children || []).map((c) => c.id).filter(Boolean);
        if (ids.length > 0) {
          setAttachedLessonIds(ids);
          setAutoAttachedCount(ids.length);
        }
      } catch (_e) {
        // Non-fatal — picker still works without pre-attach
      }
    })();
    return () => { cancelled = true; };
  }, [standalone, unitActivityId]);

  const gradeOptions = curriculum ? getGradesFromCurriculum(curriculum) : [];
  const subjectOptions = grade && curriculum ? getSubjectsForGrade(curriculum, grade) : [];
  const strandOptions = grade && subject && curriculum ? getStrandsForGradeSubject(curriculum, grade, subject) : [];

  const total = Object.values(counts).reduce((acc, n) => acc + (Number(n) || 0), 0);

  const standaloneReady = !standalone || (grade && subject);

  const handleSubmit = async () => {
    if (total === 0) {
      toast.error("Pick at least one question type to generate.");
      return;
    }
    if (standalone && !standaloneReady) {
      toast.error("Pick a grade and subject for the standalone test.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        unit_activity_id: standalone ? null : unitActivityId,
        question_counts: counts,
        expectation_codes: [],
        weighting_hint: weighting,
        attached_lesson_ids: attachedLessonIds,
      };
      if (standalone) {
        payload.grade = grade;
        payload.subject = subject;
        payload.strand = strand;
        payload.title = title.trim();
        payload.reporting_period = reportingPeriod;
      }
      const test = await generateTest(payload);
      onGenerated(test);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Generation failed";
      toast.error("Couldn't generate the test", { description: String(detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ae-card p-6" data-testid="test-setup-form">
      <p className="font-bold uppercase text-[10px] tracking-[0.22em] text-[#4a6984]/70">
        Step 1 · Setup
      </p>
      <h2 className="mt-1 font-black text-xl text-[#1c2d3d]">
        {standalone ? "Set up a standalone test" : "Pick your question types and counts"}
      </h2>
      <p className="mt-1 text-sm text-[#4a6984]">
        {standalone
          ? "Pick the grade and subject, optionally attach the lessons you've taught, then choose your question types."
          : "We'll generate a draft test grounded in the unit's curriculum expectations, tagged to the four Ontario achievement chart categories. You'll edit anything you want before printing."}
      </p>

      {standalone ? (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="test-standalone-fields">
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]/75 mb-1">Test title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Forces and Motion Unit Test"
              className="w-full h-10 rounded-md border border-[#4a6984]/25 bg-white px-3 text-sm text-[#1c2d3d]"
              data-testid="test-standalone-title"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]/75 mb-1">Reporting period</label>
            <select
              value={reportingPeriod}
              onChange={(e) => setReportingPeriod(e.target.value)}
              className="w-full h-10 rounded-md border border-[#4a6984]/25 bg-white px-3 text-sm text-[#1c2d3d]"
              data-testid="test-standalone-reporting-period"
            >
              <option value="progress_report">Progress Report (Fall)</option>
              <option value="term_1">Term 1 (Winter)</option>
              <option value="term_2">Term 2 (Spring)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]/75 mb-1">Grade *</label>
            <select
              value={grade}
              onChange={(e) => { setGrade(e.target.value); setSubject(""); setStrand(""); }}
              className="w-full h-10 rounded-md border border-[#4a6984]/25 bg-white px-3 text-sm text-[#1c2d3d]"
              data-testid="test-standalone-grade"
            >
              <option value="">Pick a grade…</option>
              {gradeOptions.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]/75 mb-1">Subject *</label>
            <select
              value={subject}
              onChange={(e) => { setSubject(e.target.value); setStrand(""); }}
              disabled={!grade}
              className="w-full h-10 rounded-md border border-[#4a6984]/25 bg-white px-3 text-sm text-[#1c2d3d] disabled:opacity-40"
              data-testid="test-standalone-subject"
            >
              <option value="">Pick a subject…</option>
              {subjectOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]/75 mb-1">Strand (optional)</label>
            <select
              value={strand}
              onChange={(e) => setStrand(e.target.value)}
              disabled={!subject}
              className="w-full h-10 rounded-md border border-[#4a6984]/25 bg-white px-3 text-sm text-[#1c2d3d] disabled:opacity-40"
              data-testid="test-standalone-strand"
            >
              <option value="">Any strand</option>
              {strandOptions.map((st) => {
                const label = typeof st === "string" ? st : `${st.code} — ${st.name}`;
                const key = typeof st === "string" ? st : st.code;
                return <option key={key} value={label}>{label}</option>;
              })}
            </select>
          </div>
        </div>
      ) : null}

      {/* Phase 119.A — Lesson grounding (visible in both modes). */}
      <div className="mt-6">
        <LessonGroundingPicker
          selectedIds={attachedLessonIds}
          onChange={setAttachedLessonIds}
          subjects={standalone ? (subject ? [subject] : []) : []}
          grade={standalone ? grade : ""}
          initialCollapsed={!standalone && autoAttachedCount > 0}
          autoAttachedHint={
            !standalone && autoAttachedCount > 0
              ? `${autoAttachedCount} lesson${autoAttachedCount === 1 ? "" : "s"} from this unit are auto-attached. The builder will ground the test in their full content.`
              : ""
          }
          emptyHint={
            standalone && !subject
              ? "Pick a subject first to see lessons you can attach."
              : "No taught lessons in this subject yet — generate one in the Generator first."
          }
        />
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        {QUESTION_TYPES.map((qt) => (
          <div
            key={qt.key}
            className="flex items-center justify-between gap-3 rounded-lg border border-[#4a6984]/15 bg-white px-4 py-3"
            data-testid={`test-type-row-${qt.key}`}
          >
            <div className="min-w-0">
              <p className="font-extrabold text-sm text-[#1c2d3d]">{qt.label}</p>
              {qt.hint ? (
                <p className="text-[11px] text-[#4a6984]/70">{qt.hint}</p>
              ) : null}
            </div>
            <input
              type="number"
              min="0"
              max="30"
              value={counts[qt.key] ?? 0}
              onChange={(e) =>
                setCounts((p) => ({ ...p, [qt.key]: Math.max(0, parseInt(e.target.value || "0", 10)) }))
              }
              className="w-20 h-10 rounded-md border border-[#4a6984]/25 bg-white text-center text-sm font-bold text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
              data-testid={`test-type-count-${qt.key}`}
            />
          </div>
        ))}
      </div>

      <div className="mt-5">
        <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]/75 mb-1">
          Weighting hint (optional)
        </label>
        <input
          type="text"
          value={weighting}
          onChange={(e) => setWeighting(e.target.value)}
          placeholder="e.g. emphasize Thinking and Application"
          className="w-full h-10 rounded-md border border-[#4a6984]/25 bg-white px-3 text-sm text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
          data-testid="test-weighting-hint"
        />
      </div>

      <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs text-[#4a6984]/75">
          Total: <span className="font-extrabold text-[#1c2d3d]">{total}</span> question
          {total === 1 ? "" : "s"}
        </p>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={busy || total === 0 || !standaloneReady}
          className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-[#1c2d3d] text-white font-bold text-sm hover:bg-[#0e1a25] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="test-generate-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {busy ? "Generating…" : "Generate test"}
        </button>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
//  Editor phase — inline question editing
// ----------------------------------------------------------------------------

const QuestionEditor = ({ q, index, onChange, onDelete }) => {
  const set = (k, v) => onChange({ ...q, [k]: v });

  return (
    <div
      className="ae-card p-5 space-y-3"
      data-testid={`test-question-${index}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="font-extrabold text-xs uppercase tracking-[0.18em] text-[#4a6984]/70">
          Q{index + 1} · {(QUESTION_TYPES.find((t) => t.key === q.type) || {}).label || q.type}
        </p>
        <div className="flex items-center gap-2">
          <select
            value={q.achievement_category}
            onChange={(e) => set("achievement_category", e.target.value)}
            className="h-8 rounded-md border border-[#4a6984]/25 bg-white px-2 text-[11px] font-bold text-[#1c2d3d]"
            data-testid={`test-question-${index}-category`}
          >
            {ACHIEVEMENT_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            type="number"
            value={q.points}
            min="1"
            max="20"
            onChange={(e) => set("points", Math.max(1, parseInt(e.target.value || "1", 10)))}
            className="h-8 w-16 rounded-md border border-[#4a6984]/25 bg-white px-2 text-[11px] font-bold text-[#1c2d3d] text-center"
            data-testid={`test-question-${index}-points`}
          />
          <button
            type="button"
            onClick={() => onDelete()}
            aria-label="Delete question"
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#4a6984] hover:text-red-600 hover:bg-red-50"
            data-testid={`test-question-${index}-delete`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <textarea
        value={q.prompt}
        onChange={(e) => set("prompt", e.target.value)}
        rows={Math.max(2, Math.ceil((q.prompt || "").length / 90))}
        className="w-full rounded-md border border-[#4a6984]/25 bg-white px-3 py-2 text-sm text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1] resize-none"
        data-testid={`test-question-${index}-prompt`}
      />
      {/* Type-specific fields */}
      {q.type === "multiple_choice" ? (
        <div className="space-y-1.5">
          {(q.options || []).map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="radio"
                name={`mc-${q.id}`}
                checked={q.correct_option_index === i}
                onChange={() => set("correct_option_index", i)}
                aria-label={`Mark option ${i + 1} correct`}
              />
              <input
                type="text"
                value={opt}
                onChange={(e) => {
                  const next = [...(q.options || [])];
                  next[i] = e.target.value;
                  set("options", next);
                }}
                className="flex-1 h-9 rounded-md border border-[#4a6984]/20 bg-white px-3 text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  const next = (q.options || []).filter((_, idx) => idx !== i);
                  set("options", next);
                }}
                className="text-[#4a6984] hover:text-red-600 px-1"
                aria-label={`Remove option ${i + 1}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => set("options", [...(q.options || []), ""])}
            className="text-xs font-bold text-[#4a6984] hover:text-[#1c2d3d] inline-flex items-center gap-1 mt-1"
          >
            <Plus className="h-3 w-3" /> Add option
          </button>
        </div>
      ) : null}
      {q.type === "true_false" ? (
        <div className="flex items-center gap-3 text-sm font-bold text-[#1c2d3d]">
          Answer:
          <label className="inline-flex items-center gap-1">
            <input
              type="radio"
              checked={q.correct_bool === true}
              onChange={() => set("correct_bool", true)}
            />
            True
          </label>
          <label className="inline-flex items-center gap-1">
            <input
              type="radio"
              checked={q.correct_bool === false}
              onChange={() => set("correct_bool", false)}
            />
            False
          </label>
        </div>
      ) : null}
      {q.type === "matching" ? (
        <div className="space-y-1.5">
          {(q.pairs || []).map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={p.left || ""}
                onChange={(e) => {
                  const next = [...(q.pairs || [])];
                  next[i] = { ...next[i], left: e.target.value };
                  set("pairs", next);
                }}
                placeholder="Left prompt"
                className="flex-1 h-9 rounded-md border border-[#4a6984]/20 bg-white px-3 text-sm"
              />
              <span className="text-[#4a6984]/50">→</span>
              <input
                type="text"
                value={p.right || ""}
                onChange={(e) => {
                  const next = [...(q.pairs || [])];
                  next[i] = { ...next[i], right: e.target.value };
                  set("pairs", next);
                }}
                placeholder="Right answer"
                className="flex-1 h-9 rounded-md border border-[#4a6984]/20 bg-white px-3 text-sm"
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() => set("pairs", [...(q.pairs || []), { left: "", right: "" }])}
            className="text-xs font-bold text-[#4a6984] hover:text-[#1c2d3d] inline-flex items-center gap-1 mt-1"
          >
            <Plus className="h-3 w-3" /> Add pair
          </button>
        </div>
      ) : null}
      {q.type === "labeling" ? (
        <div className="space-y-2">
          <textarea
            value={q.diagram_description || ""}
            onChange={(e) => set("diagram_description", e.target.value)}
            placeholder="Describe the diagram the teacher should draw…"
            rows={2}
            className="w-full rounded-md border border-[#4a6984]/20 bg-white px-3 py-2 text-sm resize-none"
          />
          {(q.labels || []).map((l, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={l}
                onChange={(e) => {
                  const next = [...(q.labels || [])];
                  next[i] = e.target.value;
                  set("labels", next);
                }}
                className="flex-1 h-9 rounded-md border border-[#4a6984]/20 bg-white px-3 text-sm"
              />
              <button
                type="button"
                onClick={() => set("labels", (q.labels || []).filter((_, idx) => idx !== i))}
                className="text-[#4a6984] hover:text-red-600 px-1"
                aria-label="Remove label"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => set("labels", [...(q.labels || []), ""])}
            className="text-xs font-bold text-[#4a6984] hover:text-[#1c2d3d] inline-flex items-center gap-1"
          >
            <Plus className="h-3 w-3" /> Add label
          </button>
        </div>
      ) : null}
      {(q.type === "short_answer" || q.type === "long_answer") ? (
        <div className="space-y-2">
          <div>
            <label className="block text-[10px] uppercase font-bold tracking-[0.18em] text-[#4a6984]/70 mb-1">
              Model answer
            </label>
            <textarea
              value={q.expected_answer || ""}
              onChange={(e) => set("expected_answer", e.target.value)}
              rows={q.type === "long_answer" ? 4 : 2}
              className="w-full rounded-md border border-[#4a6984]/20 bg-white px-3 py-2 text-sm resize-none"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-bold tracking-[0.18em] text-[#4a6984]/70 mb-1">
              Marking guide
            </label>
            <textarea
              value={q.marking_guide || ""}
              onChange={(e) => set("marking_guide", e.target.value)}
              rows={3}
              className="w-full rounded-md border border-[#4a6984]/20 bg-white px-3 py-2 text-sm resize-none"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ----------------------------------------------------------------------------
//  Page
// ----------------------------------------------------------------------------

const TestBuilder = () => {
  const { user, loading: authLoading } = useAuth();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const unitActivityId = searchParams.get("unit_activity_id");
  const standalone = searchParams.get("standalone") === "1";
  const existingTestId = params.testId;

  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(!!existingTestId);
  const [saving, setSaving] = useState(false);
  const [regen, setRegen] = useState(false);
  // Phase 122 — Save-to-folder modal. We open it once, immediately
  // after a generation succeeds, with the just-created test's Library
  // stub so the teacher confirms (or overrides) where it was filed.
  const [savedStub, setSavedStub] = useState(null);
  const [folders, setFolders] = useState([]);

  useEffect(() => {
    if (!existingTestId) return undefined;
    let cancelled = false;
    (async () => {
      // Hoist setLoading inside the async IIFE so React-19's
      // set-state-in-effect rule doesn't fire.
      if (cancelled) return;
      setLoading(true);
      try {
        const d = await getTest(existingTestId);
        if (cancelled) return;
        setTest(d);
      } catch (_err) {
        if (cancelled) return;
        toast.error("Couldn't load that test");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [existingTestId]);

  const updateQuestion = (idx, q) => {
    setTest((prev) => {
      if (!prev) return prev;
      const next = [...(prev.questions || [])];
      next[idx] = q;
      return { ...prev, questions: next };
    });
  };

  const deleteQuestion = (idx) => {
    setTest((prev) => {
      if (!prev) return prev;
      const next = (prev.questions || []).filter((_, i) => i !== idx);
      return { ...prev, questions: next };
    });
  };

  const handleSave = async () => {
    if (!test) return;
    setSaving(true);
    try {
      const fresh = await saveTest(test.id, {
        title: test.title,
        questions: test.questions,
      });
      setTest(fresh);
      toast.success("Test saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (!test) return;
    if (!window.confirm("Re-generate from scratch? This replaces all current questions.")) return;
    setRegen(true);
    try {
      const fresh = await regenerateTest(test.id);
      setTest(fresh);
      toast.success("Regenerated");
    } catch (e) {
      toast.error("Regenerate failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setRegen(false);
    }
  };

  const handleExport = async (kind) => {
    if (!test) return;
    try {
      const res = await api.get(`/tests/${test.id}/${kind}.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${test.title || "Test"}_${kind}.pdf`.replace(/\s+/g, "_");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed", { description: e?.response?.data?.detail || e?.message });
    }
  };

  const totalPoints = useMemo(
    () => (test?.questions || []).reduce((acc, q) => acc + (Number(q.points) || 1), 0),
    [test?.questions],
  );

  if (!authLoading && !user) return <Navigate to="/" replace />;

  return (
    <AppShell title="Test Builder" subtitle={test?.title || "Generate a unit test"}>
      <div className="bg-[#f4f5f7] min-h-full">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <a
                href={unitActivityId ? `/lesson/${unitActivityId}` : "/dashboard"}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-[#4a6984] hover:text-[#1c2d3d]"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </a>
            </div>
            {test ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="ae-pill" data-testid="test-total-points">
                  {totalPoints} marks · {(test.questions || []).length} questions
                </span>
                <button
                  type="button"
                  onClick={handleRegenerate}
                  disabled={regen}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#4a6984]/25 bg-white text-xs font-bold text-[#1c2d3d] hover:bg-[#f4f5f7] disabled:opacity-50"
                  data-testid="test-regenerate-btn"
                >
                  {regen ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  {regen ? "Regenerating…" : "Regenerate"}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#4a6984]/25 bg-white text-xs font-bold text-[#1c2d3d] hover:bg-[#f4f5f7] disabled:opacity-50"
                  data-testid="test-save-btn"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  {saving ? "Saving…" : "Save edits"}
                </button>
                <a
                  href={`/tests/${test.id}/mark`}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#4a6984]/25 bg-white text-xs font-bold text-[#1c2d3d] hover:bg-[#f4f5f7]"
                  data-testid="test-mark-students-btn"
                >
                  <ClipboardCheck className="h-3.5 w-3.5" />
                  Mark students
                </a>
                <button
                  type="button"
                  onClick={() => handleExport("student")}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#4a6984]/25 bg-white text-xs font-bold text-[#1c2d3d] hover:bg-[#f4f5f7]"
                  data-testid="test-export-student-btn"
                >
                  <Download className="h-3.5 w-3.5" />
                  Student PDF
                </button>
                <button
                  type="button"
                  onClick={() => handleExport("teacher")}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#1c2d3d] text-white text-xs font-bold hover:bg-[#0e1a25]"
                  data-testid="test-export-teacher-btn"
                >
                  <Download className="h-3.5 w-3.5" />
                  Teacher PDF
                </button>
              </div>
            ) : null}
          </div>

          {/* Body */}
          {loading ? (
            <div className="ae-card p-10 flex items-center justify-center text-[#4a6984]">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading test…
            </div>
          ) : !test ? (
            (unitActivityId || standalone) ? (
              <SetupForm
              unitActivityId={unitActivityId}
              standalone={standalone}
              onGenerated={async (fresh) => {
                // Phase 118.A.2 — if the Test was started from the
                // Unit Summative fork, the sessionStorage bridge
                // tells us to attach this test back to the unit.
                const pending = _readPendingAttach();
                if (pending && pending.unit_activity_id === fresh.unit_activity_id) {
                  try {
                    await _api.post(
                      `/unit-summative/${encodeURIComponent(fresh.unit_activity_id)}/attach-test`,
                      { test_id: fresh.id, assessment_name: fresh.title || "" },
                    );
                  } catch (e) {
                    // Don't block — the teacher can still edit/print
                    // the test. The unit-summative state will catch
                    // up on next reload of the unit page.
                    toast.error("Couldn't link the test to your unit. Open the unit to retry.", {
                      description: e?.response?.data?.detail || e?.message,
                    });
                  }
                  _clearPendingAttach();
                }
                setTest(fresh);
                window.history.replaceState({}, "", `/tests/${fresh.id}`);
                // Phase 122 — fetch the Library stub for this test so
                // the Save-to-folder modal can show the auto-filed
                // location. Fire-and-forget; if it fails we just
                // skip the modal (the test is still saved).
                try {
                  const [acts, fs] = await Promise.all([
                    listActivities(),
                    listFolders(),
                  ]);
                  const stub = (acts || []).find(
                    (a) => a.linked_test_id === fresh.id
                  );
                  if (stub) {
                    setFolders(fs || []);
                    setSavedStub(stub);
                  }
                } catch (_err) {
                  /* Modal is a nice-to-have; never block the flow. */
                }
              }}
            />
            ) : (
              <div className="ae-card p-8 text-center text-sm text-[#4a6984]">
                Open a unit and click <strong>Build a test</strong> — or use <a href="/assess/build" className="text-[#1c9e7e] font-bold underline">Assess &amp; Report → Build a Summative</a> to start a standalone test.
              </div>
            )
          ) : (
            <div className="space-y-4">
              <div className="ae-card p-4">
                <label className="block text-[10px] uppercase font-bold tracking-[0.18em] text-[#4a6984]/70 mb-1">
                  Test title
                </label>
                <input
                  type="text"
                  value={test.title || ""}
                  onChange={(e) => setTest((p) => ({ ...p, title: e.target.value }))}
                  className="w-full h-10 rounded-md border border-[#4a6984]/25 bg-white px-3 text-base font-bold text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
                  data-testid="test-title-input"
                />
              </div>
              {(test.questions || []).map((q, i) => (
                <QuestionEditor
                  key={q.id || i}
                  q={q}
                  index={i}
                  onChange={(updated) => updateQuestion(i, updated)}
                  onDelete={() => deleteQuestion(i)}
                />
              ))}
              {(test.questions || []).length === 0 ? (
                <div className="ae-card p-6 text-center text-sm text-[#4a6984]/70">
                  No questions. Use <strong>Regenerate</strong> to draft a fresh set.
                </div>
              ) : null}
            </div>
          )}
        </main>
      </div>
      <SaveToFolderModal
        open={!!savedStub}
        activity={savedStub}
        folders={folders}
        onClose={() => setSavedStub(null)}
        onMoved={() => {/* nothing else to do — list will refetch on Library nav */}}
      />
    </AppShell>
  );
};

export default TestBuilder;
